/**
 * ÇOK DİLLİ ARAYÜZ — dil seçimi ve tek sözlük (kullanıcı kararı 27.10.2026;
 * `claude/notlar/2026-10-27-cok-dilli-arayuz-kararlar.md`).
 *
 * - Dil ilk açılışta tarayıcının dilinden seçilir; Ayarlar'dan değişir.
 *   Seçim YALNIZCA bu cihazın `localStorage`ında durur (tema gibi bir sunum
 *   tercihi); merkeze giden hiçbir isteğe girmez.
 * - Dil bir sayfa yüklemesi boyunca SABİTTİR: değiştirmek sayfayı yeniden
 *   yükler (kısa süreli oturum açıksa kasa parola sormadan açılır). Bu
 *   sayede modül düzeyindeki etiket tabloları da doğru dilde kurulur ve
 *   ekranın yarısı eski dilde kalamaz.
 * - Sözlüğün şekli Türkçedir; İngilizce aynı şekle derlemede bağlıdır.
 *   Sonraki dillerde eksik anahtar İngilizceden gelir.
 * - Tarih ve sayılar dilin yerel biçimiyle gösterilir; hesaplama değişmez.
 */

import { en } from "./en";
import { tr } from "./tr";
import { bilerekYenile } from "../lib/sayfaCikisi";

export type DilKodu = "tr" | "en";

export interface DilBilgisi {
  readonly kod: DilKodu;
  /** Dilin KENDİ dilindeki adı — seçicide her zaman böyle görünür. */
  readonly ad: string;
  /** `Intl` yerel kodu (tarih, sayı, para birimi adları). */
  readonly yerel: string;
}

/**
 * Seçilebilen diller. İngilizce için `en-GB`: gün/ay/yıl sırası Türkçeyle
 * aynı (ABD'nin ay/gün sırası Avrupa'da yanlış okunur), sayılar 1,234.56.
 */
export const DILLER: readonly DilBilgisi[] = [
  { kod: "tr", ad: "Türkçe", yerel: "tr-TR" },
  { kod: "en", ad: "English", yerel: "en-GB" },
];

export const DIL_ANAHTARI = "parafon.dil";

export type Sozluk = typeof tr;

const SOZLUKLER: Readonly<Record<DilKodu, Sozluk>> = { tr, en };

function desteklenen(kod: string | null | undefined): DilKodu | null {
  if (typeof kod !== "string") return null;
  const k = kod.toLowerCase().split(/[-_]/)[0];
  return DILLER.some((d) => d.kod === k) ? (k as DilKodu) : null;
}

/**
 * Kayıtlı tercih; yoksa tarayıcının dil listesinden ilk desteklenen; o da
 * yoksa İngilizce (kararda: eksik çeviride İngilizce görünür).
 */
export function dilBelirle(
  kayitli: string | null,
  tarayici: readonly string[],
): DilKodu {
  const k = desteklenen(kayitli);
  if (k !== null) return k;
  for (const t of tarayici) {
    const d = desteklenen(t);
    if (d !== null) return d;
  }
  return "en";
}

function kayitliDil(): string | null {
  try {
    return globalThis.localStorage?.getItem(DIL_ANAHTARI) ?? null;
  } catch {
    return null;
  }
}

function tarayiciDilleri(): readonly string[] {
  const n = globalThis.navigator;
  if (n === undefined) return [];
  if (Array.isArray(n.languages) && n.languages.length > 0) return n.languages;
  return typeof n.language === "string" ? [n.language] : [];
}

/** Bu sayfa yüklemesinin dili. */
export const DIL: DilKodu = dilBelirle(kayitliDil(), tarayiciDilleri());

/** Bu sayfa yüklemesinin `Intl` yereli. */
export const YEREL: string = (DILLER.find((d) => d.kod === DIL) ?? DILLER[0]!).yerel;

/** Etkin sözlük. Ekranlar metni yalnızca buradan okur. */
export const M: Sozluk = SOZLUKLER[DIL];

/** Dili değiştirir: tercih cihaza yazılır, sayfa yeniden yüklenir. */
export function dilDegistir(kod: DilKodu): void {
  if (kod === DIL) return;
  try {
    globalThis.localStorage?.setItem(DIL_ANAHTARI, kod);
  } catch {
    // Depolama kapalı (gizli pencere): tercih kalıcı olamaz; yine de
    // yeniden yükleme tarayıcının diline döner. Kabul edilebilir.
  }
  bilerekYenile();
}

if (globalThis.document?.documentElement !== undefined) {
  globalThis.document.documentElement.lang = DIL;
}

export { SOZLUKLER };
